import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2, ArrowLeft, ListMusic, Clock } from "lucide-react";
import PublicShell from "@/components/PublicShell";
import TrackCard from "@/components/TrackCard";
import SocialShare from "@/components/SocialShare";
import ArtistPlaylist from "@/components/ArtistPlaylist";
import EmbedButton from "@/components/EmbedButton";
import api from "@/lib/api";
import { useSeo } from "@/lib/seo";

// Public shared playlist page (/playlist/:id).
export default function PlaylistPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => { api.get(`/playlists/${id}`).then(({ data: d }) => setData(d)).catch((e) => setError(e.response?.data?.detail || "Playlist not found")); }, [id]);
  const patch = (t) => setData((d) => d && { ...d, tracks: d.tracks.map((x) => (x.id === t.id ? { ...x, likes: t.likes, liked: t.liked } : x)) });
  useSeo({ title: data ? `${data.title} — MusicFlow playlist` : "Playlist", path: `/playlist/${id}`, description: data ? `${data.tracks.length} AI game music tracks curated on MusicFlow StudioAi.` : undefined });
  const mins = data ? Math.round(data.total_seconds / 60) : 0;
  return (
    <PublicShell active="showcase">
      <div className="max-w-7xl mx-auto px-6 pt-12 pb-24" data-testid="playlist-page">
        <Link to="/showcase" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="playlist-back"><ArrowLeft size={14} /> Back to showcase</Link>
        {error && <div className="mt-10 rounded-2xl border border-border p-12 text-center text-muted-foreground" data-testid="playlist-error">{error}</div>}
        {!data && !error && <div className="flex justify-center py-24 text-muted-foreground"><Loader2 className="animate-spin" /></div>}
        {data && (
          <>
            <span className="mt-8 inline-flex items-center gap-2 text-xs font-mono px-3 py-1.5 rounded-full border border-fuchsia-500/40 bg-fuchsia-500/10 text-fuchsia-200"><ListMusic size={12} /> Shared playlist</span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold mt-4 leading-[0.98]" data-testid="playlist-title">{data.title}</h1>
            <p className="text-muted-foreground mt-3 text-sm inline-flex items-center gap-2" data-testid="playlist-meta"><Clock size={13} /> {data.tracks.length} track{data.tracks.length > 1 ? "s" : ""} · ~{mins} min · {[...new Set(data.tracks.map((t) => t.artist))].length} creator(s)</p>
            <div className="mt-6 flex flex-wrap items-center gap-4">
              <ArtistPlaylist tracks={data.tracks} name={data.title} onStart={() => api.post(`/playlists/${id}/play`).catch(() => {})} />
              <SocialShare url={`${window.location.origin}/playlist/${id}`} title={`${data.title} — AI game music playlist on MusicFlow StudioAi`} lang="en" testId="playlist-social" track={{ kind: "playlist", ref: id }} />
              <EmbedButton path={data.embed_url} testId="playlist-embed" defaultAccent="#e879f9" />
            </div>
            <div className="mt-12 grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="playlist-tracks">{data.tracks.map((t, i) => <TrackCard key={t.id} t={t} i={i} onChange={patch} />)}</div>
          </>
        )}
      </div>
    </PublicShell>
  );
}
